// Seção Fiscal (NF-e) da order: um card por NF vinculada (venda/devolução),
// check de valor vNF×venda, itens da nota, impostos (detalhe sob demanda) e
// visualização/download do XML. NFs internas do Full (remessa/retorno de
// depósito) não aparecem — só uma contagem discreta.
//
// Protótipo: o XML vem do acervo LOCAL (D:\ml-nfe) via backend; no futuro os
// workers salvam no S3 e nada muda aqui além do backend.
import { useState } from 'react';
import api from '../../services/api';
import { FormatDateTimeBr } from '../../utils/date';
import { FormatMoney } from '../../utils/format';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Modal from '../ui/Modal';

const money = (v) => FormatMoney(v);
const dateTime = (v) => FormatDateTimeBr(v);

function useXml(chave) {
    const [state, setState] = useState({ open: false, xml: null, loading: false, error: null });
    const view = async () => {
        setState((s) => ({ ...s, open: true, loading: true, error: null }));
        try {
            const xml = await api.getFiscalXml(chave);
            setState((s) => ({ ...s, xml, loading: false }));
        } catch (err) {
            setState((s) => ({ ...s, loading: false, error: err.message }));
        }
    };
    const download = async () => {
        try {
            const xml = state.xml || (await api.getFiscalXml(chave));
            const blob = new Blob([xml], { type: 'application/xml' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${chave}.xml`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setState((s) => ({ ...s, open: true, error: err.message }));
        }
    };
    const close = () => setState((s) => ({ ...s, open: false }));
    return { state, view, download, close };
}

// XML com quebras amigáveis pra leitura no modal (sem lib externa).
const prettyXml = (xml) => xml.replace(/></g, '>\n<');

function InvoiceCard({ inv, valueCheck, orderTotal, fiscalDeep, onLoadDeep }) {
    const { state, view, download, close } = useXml(inv.chave);
    const cancelled = inv.hasCancelEvent || inv.statusCodigo !== '100';
    const isDevolucao = inv.kind === 'DEVOLUCAO';
    const badge = cancelled ? (
        <Badge variant="danger">CANCELADA</Badge>
    ) : inv.statusCodigo === '100' ? (
        <Badge variant="success">AUTORIZADA</Badge>
    ) : (
        <Badge variant="neutral">cStat {inv.statusCodigo}</Badge>
    );
    const taxes = fiscalDeep?.taxes?.filter((t) => inv.items.some((i) => i.id === t.nfe_item_id)) || null;
    const totals = inv.totals;

    return (
        <div className={`rounded-xl border-2 bg-white p-4 dark:bg-mono-900 ${cancelled ? 'border-red-300 dark:border-red-800' : 'border-violet-300/60 dark:border-violet-800'}`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="font-mono text-lg font-bold text-violet-600 dark:text-violet-400" title={`nfe_documents.numero/serie (${inv.matchType})`}>
                    {isDevolucao ? 'NF de devolução' : 'NF-e'} {inv.numero}/{inv.serie}
                </span>
                {badge}
                <span className="text-xs text-mono-400">
                    emitida {dateTime(inv.dataEmissao)}
                    {inv.dataAutorizacao ? ` · autorizada ${dateTime(inv.dataAutorizacao)}` : ''}
                </span>
                <div className="ml-auto flex gap-2">
                    <Button variant="secondary" size="sm" onClick={view}>
                        Ver XML
                    </Button>
                    <Button variant="secondary" size="sm" onClick={download}>
                        Baixar XML
                    </Button>
                </div>
            </div>

            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="font-mono text-violet-600 dark:text-violet-400" title="chave de acesso (44 dígitos)">
                    {inv.chave}
                </span>
                <button
                    type="button"
                    className="rounded border border-mono-200 px-1.5 py-0.5 text-mono-500 hover:bg-mono-50 dark:border-mono-700 dark:hover:bg-mono-800"
                    onClick={() => navigator.clipboard?.writeText(inv.chave)}
                >
                    copiar
                </button>
                <span className="text-mono-400">{inv.naturezaOperacao}</span>
            </div>

            {cancelled && inv.cancelEventDate ? (
                <p className="mb-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                    Cancelamento fiscal registrado em {dateTime(inv.cancelEventDate)} (evento 110111).
                </p>
            ) : null}

            <div className="mb-2 grid grid-cols-2 gap-2 text-sm md:grid-cols-5">
                {[
                    ['vProd', inv.valorProdutos, 'valor_produtos'],
                    ['vFrete', inv.valorFrete, 'valor_frete'],
                    ['vDesc', inv.valorDesconto, 'valor_desconto'],
                    ['vNF', inv.valorTotal, 'valor_total_nf'],
                ].map(([label, v, path]) => (
                    <div key={label}>
                        <p className="text-xs text-mono-500">{label}</p>
                        <p className="font-mono font-semibold text-violet-600 dark:text-violet-400" title={`nfe_documents.${path}`}>
                            {money(v)}
                        </p>
                    </div>
                ))}
                {!isDevolucao && valueCheck ? (
                    <div>
                        <p className="text-xs text-mono-500">Conciliação</p>
                        {valueCheck.status === 'ok' ? (
                            <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400" title={valueCheck.formula}>
                                = venda ✓
                            </p>
                        ) : (
                            <p className="text-sm font-semibold text-yellow-600 dark:text-yellow-400" title={`vNF ${money(valueCheck.vNF)} vs venda ${money(valueCheck.expected)}`}>
                                Δ {money(valueCheck.delta)}
                            </p>
                        )}
                    </div>
                ) : null}
            </div>

            {totals ? (
                <p className="mb-2 text-xs text-mono-500">
                    Impostos:{' '}
                    <span className="font-mono text-violet-600 dark:text-violet-400" title="nfe_document_totals">
                        ICMS {money(totals.v_icms)} · PIS {money(totals.v_pis)} · COFINS {money(totals.v_cofins)}
                        {Number(totals.v_tot_trib) > 0 ? ` · total aprox. tributos ${money(totals.v_tot_trib)}` : ''}
                    </span>
                </p>
            ) : null}

            {inv.items?.length ? (
                <details className="mb-1">
                    <summary className="cursor-pointer text-xs text-mono-500">Itens da NF ({inv.items.length})</summary>
                    <div className="mt-1 overflow-x-auto">
                        <table className="w-full min-w-[560px] text-xs">
                            <thead>
                                <tr className="text-left text-mono-500">
                                    <th className="py-1 pr-2">cProd</th>
                                    <th className="pr-2">descrição</th>
                                    <th className="pr-2 text-right">qtd</th>
                                    <th className="pr-2 text-right">vUnit</th>
                                    <th className="pr-2 text-right">vProd</th>
                                    <th className="pr-2">NCM</th>
                                    <th>CFOP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inv.items.map((it) => (
                                    <tr key={it.id} className="border-t border-mono-100 font-mono text-violet-600 dark:border-mono-800 dark:text-violet-400">
                                        <td className="py-1 pr-2">{it.codigo_produto}</td>
                                        <td className="max-w-[220px] truncate pr-2" title={it.descricao}>
                                            {it.descricao}
                                        </td>
                                        <td className="pr-2 text-right">{Number(it.quantidade_comercial)}</td>
                                        <td className="pr-2 text-right">{money(it.valor_unitario_comercial)}</td>
                                        <td className="pr-2 text-right">{money(it.valor_produto)}</td>
                                        <td className="pr-2">{it.ncm}</td>
                                        <td>{it.cfop}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="mt-1 text-[11px] text-mono-400">
                            O cProd da NF pode diferir do SKU do anúncio (unidade de embalagem/apelido interno) — confira pelo valor.
                        </p>
                    </div>
                </details>
            ) : null}

            <details onToggle={(e) => e.target.open && !fiscalDeep && onLoadDeep()}>
                <summary className="cursor-pointer text-xs text-mono-500">Impostos por item (CST/base/alíquota)</summary>
                {!fiscalDeep ? (
                    <p className="mt-1 text-xs text-mono-400">Carregando…</p>
                ) : taxes?.length ? (
                    <div className="mt-1 overflow-x-auto">
                        <table className="w-full min-w-[520px] text-xs">
                            <thead>
                                <tr className="text-left text-mono-500">
                                    <th className="py-1 pr-2">item</th>
                                    <th className="pr-2">tributo</th>
                                    <th className="pr-2">grupo</th>
                                    <th className="pr-2">CST</th>
                                    <th className="pr-2 text-right">base</th>
                                    <th className="pr-2 text-right">alíq.</th>
                                    <th className="text-right">valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {taxes.map((t) => (
                                    <tr key={t.id} className="border-t border-mono-100 font-mono text-violet-600 dark:border-mono-800 dark:text-violet-400">
                                        <td className="py-1 pr-2">{inv.items.findIndex((i) => i.id === t.nfe_item_id) + 1}</td>
                                        <td className="pr-2">{t.tax_type}</td>
                                        <td className="pr-2">{t.tax_group || '—'}</td>
                                        <td className="pr-2">{t.cst || t.csosn || '—'}</td>
                                        <td className="pr-2 text-right">{t.base_calculo != null ? money(t.base_calculo) : '—'}</td>
                                        <td className="pr-2 text-right">{t.aliquota != null ? `${Number(t.aliquota)}%` : '—'}</td>
                                        <td className="text-right">{t.valor != null ? money(t.valor) : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="mt-1 text-xs text-mono-400">Sem detalhamento de impostos para esta NF.</p>
                )}
            </details>

            <Modal isOpen={state.open} onClose={close} title={`XML — NF-e ${inv.numero}/${inv.serie}`} size="4xl">
                {state.loading ? (
                    <p className="text-sm text-mono-400">Baixando XML do acervo…</p>
                ) : state.error ? (
                    <p className="text-sm text-red-600">{state.error}</p>
                ) : (
                    <pre className="max-h-[65vh] overflow-auto rounded bg-mono-50 p-3 font-mono text-xs leading-relaxed text-mono-700 dark:bg-mono-800 dark:text-mono-300">
                        {state.xml ? prettyXml(state.xml) : ''}
                    </pre>
                )}
            </Modal>
        </div>
    );
}

export default function FiscalSection({ analysis, orderId }) {
    const [deep, setDeep] = useState(null);
    const [deepLoading, setDeepLoading] = useState(false);
    const loadDeep = async () => {
        if (deep || deepLoading) {
            return;
        }
        setDeepLoading(true);
        try {
            setDeep(await api.getOrderFiscal(orderId));
        } catch {
            setDeep({ taxes: [], events: [], references: [] });
        } finally {
            setDeepLoading(false);
        }
    };

    const fiscal = analysis?.fiscal;
    const completenessFiscal = analysis?.completeness?.fiscal;
    const cards = [...(fiscal?.vendas || []), ...(fiscal?.devolucoes || [])];

    return (
        <div className="space-y-3">
            {cards.length === 0 ? (
                completenessFiscal === 'N/A' ? (
                    <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
                        Sem NF-e emitida — correto para pedido cancelado sem captura.
                    </p>
                ) : ['PENDING_SLA', 'ACERVO_NOT_COVERED'].includes(completenessFiscal) ? (
                    <p className="rounded-lg bg-mono-100 p-3 text-sm text-mono-500 dark:bg-mono-800">
                        Aguardando NF-e{completenessFiscal === 'ACERVO_NOT_COVERED' ? ' — o acervo local de XMLs ainda não cobre a data desta order' : ' (dentro do prazo normal de emissão)'}
                        .
                    </p>
                ) : (
                    <p className="rounded-lg bg-yellow-50 p-3 text-sm text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">
                        Nenhuma NF-e de venda localizada para esta order (veja os alertas).
                    </p>
                )
            ) : (
                cards.map((inv) => (
                    <InvoiceCard
                        key={inv.chave || inv.docId}
                        inv={inv}
                        valueCheck={inv.kind === 'VENDA' ? fiscal?.valueCheck : null}
                        fiscalDeep={deep}
                        onLoadDeep={loadDeep}
                    />
                ))
            )}
            {fiscal?.internasCount > 0 ? (
                <p className="text-xs text-mono-400">
                    + {fiscal.internasCount} NF(s) de movimentação interna do Full relacionadas (remessa/retorno de depósito) — não são notas de venda.
                </p>
            ) : null}
        </div>
    );
}
